import { makeAutoObservable } from 'mobx';
import { RegisterValidator } from '../validators/RegisterValidator';
import {
  IAuthDataSource,
  RegisterStep,
  PHONE_VERIFICATION_ERROR_KEY,
  UNEXPECTED_ERROR_KEY,
  type ConfirmationMethod,
  type PhoneVerificationError,
  IncomingPhone,
  RegistrationGoal,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import type { IMetrikaService } from '../../../../../../shared/infrastructure/types/common/metrika-types';
import { phoneUtils } from '../../../../../../shared/infrastructure/utils/phone-utils';
import {
  LoadingStatus,
  SubmittingStatus,
} from '../../../../../../shared/infrastructure/types/common/common-types';
import { ResponseStatus } from '../../../../../../shared/infrastructure/types/api/api-types';
import type {
  RegisterRequest,
  RegisterErrors,
} from '../../../../../infrastructure/types/features/auth/auth-contracts';
import type { IAppLoaderStorage } from '../../../../../infrastructure/types/utils/storage-types';
import { YandexCloudManager } from './YandexCloudManager';
import { OutgoingManager } from './OutgoingManager';
import { QrConfirmationManager } from './QrConfirmationManager';
import { IncomingConfirmationManager } from './IncomingConfirmationManager';
import { TrackingManager } from './TrackingManager';

class RegisterManager {
  phone?: PhoneNumber;
  phoneCode?: PhoneCode;
  codeError?: string;
  companyName?: CompanyName;
  fio?: FIO;
  login?: LoginEmail;
  password?: LoginPassword;
  promoCode?: PromoCode;
  isOperator?: IsOperator = false as IsOperator;
  smartCaptchaToken?: SmartCaptchaToken;
  registerErrors?: RegisterErrors;
  registerGeneralError?: string;
  registrationId?: string;

  incomingPhone = IncomingPhone.TEST;

  confirmationMethod: ConfirmationMethod = 'outgoing';
  verificationError?: string;

  step: RegisterStep = RegisterStep.ENTER_PHONE;
  private isPhoneVerified = false;

  validator: RegisterValidator;

  readonly trackingManager: TrackingManager;

  outgoingManager: OutgoingManager;
  qrConfirmationManager: QrConfirmationManager;
  incomingConfirmationManager: IncomingConfirmationManager;

  constructor(
    private datasource: IAuthDataSource,
    private yandexCloudManager: YandexCloudManager,
    private readonly appStorage: IAppLoaderStorage,
    private getFormStatus: () => SubmittingStatus,
    private setFormStatus: (status: SubmittingStatus) => void,
    private getEnterPhoneLoadingStatus: () => LoadingStatus,
    private setEnterPhoneLoadingStatus: (status: LoadingStatus) => void,
    private autoLogin: (email: LoginEmail, password: LoginPassword) => void,
    private getVerificationPrepareStatus: () => LoadingStatus,
    private setVerificationPrepareStatus: (status: LoadingStatus) => void,
    private readonly metrikaService: IMetrikaService<RegistrationGoal>,
  ) {
    if (window.location.hostname === 'app.skorozvon.ru') {
      this.incomingPhone = IncomingPhone.PROD;
    }
    makeAutoObservable(this, {});
    this.validator = new RegisterValidator(this);
    this.trackingManager = new TrackingManager(this.appStorage);

    this.outgoingManager = new OutgoingManager(
      this,
      yandexCloudManager,
      datasource,
      setEnterPhoneLoadingStatus,
      setFormStatus,
    );
    this.qrConfirmationManager = new QrConfirmationManager(
      this,
      yandexCloudManager,
      datasource,
      setVerificationPrepareStatus,
      setFormStatus,
    );
    this.incomingConfirmationManager = new IncomingConfirmationManager(
      this,
      yandexCloudManager,
      datasource,
      setVerificationPrepareStatus,
      setFormStatus,
    );
  }

  get verificationErrorKey(): string | undefined {
    if (!this.verificationError) {
      return undefined;
    }
    return (
      PHONE_VERIFICATION_ERROR_KEY[this.verificationError as PhoneVerificationError] ??
      UNEXPECTED_ERROR_KEY
    );
  }

  get formStatus() {
    return this.getFormStatus();
  }

  get enterPhoneLoading() {
    return this.getEnterPhoneLoadingStatus();
  }

  get verificationPrepareStatus() {
    return this.getVerificationPrepareStatus();
  }

  setStep(step: RegisterStep) {
    this.step = step;
  }

  markPhoneVerified() {
    if (!this.isPhoneVerified) {
      this.isPhoneVerified = true;
      this.metrikaService.reachGoal(
        RegistrationGoal.PHONE_SUBMIT,
        this.trackingManager.registrationStartPage,
      );
    }
    this.setStep(RegisterStep.FORM);
  }

  setPhone(phone: PhoneNumber) {
    this.phone = phone;
  }

  openChangePhoneForm() {
    this.outgoingManager.stopConfirmationTimer();
    this.setStep(RegisterStep.CHANGE_PHONE);
  }

  confirmationNewPhone() {
    this.outgoingManager.setIsWasRequest(false);
    this.setStep(RegisterStep.CONFIRM_PHONE);
  }

  setCompanyName(companyName: CompanyName) {
    this.companyName = companyName;
    this.clearRegisterErrors();
  }

  setFio(fio: FIO) {
    this.fio = fio;
    this.clearRegisterErrors();
  }

  setLogin(login: LoginEmail) {
    this.login = login;
    this.clearRegisterErrors();
  }

  setPassword(password: LoginPassword) {
    this.password = password;
    this.clearRegisterErrors();
  }

  setPromoCode(promoCode: PromoCode) {
    this.promoCode = promoCode;
    this.clearRegisterErrors();
  }

  setOperator(value: boolean) {
    this.isOperator = value as IsOperator;
  }

  setSmartCaptchaToken(token: SmartCaptchaToken) {
    this.smartCaptchaToken = token;
    if (this.step === RegisterStep.ENTER_PHONE && this.phone) {
      this.submitPhoneEnter();
    }
  }

  setPhoneCode(phoneCode: PhoneCode) {
    this.phoneCode = phoneCode;
    this.codeError = undefined;
  }

  clearRegisterErrors() {
    this.registerErrors = undefined;
    this.registerGeneralError = undefined;
    this.yandexCloudManager.clearRegistrationError();
  }

  setConfirmationMethod(method: ConfirmationMethod) {
    if (!this.yandexCloudManager.refreshValidation()) {
      this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
      return;
    }

    this.confirmationMethod = method;
  }

  submitPhoneEnter() {
    this.yandexCloudManager.clearRegistrationError();

    if (!this.yandexCloudManager.refreshValidation()) {
      return this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
    }

    if (this.phone) {
      this.phone = phoneUtils.normalizePhone(this.phone) as PhoneNumber;
    }

    if (this.validator.hasPhoneEnterErrors) {
      return this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
    }

    if (this.step === RegisterStep.ENTER_PHONE) {
      this.metrikaService.reachGoal(
        RegistrationGoal.PHONE_ENTER,
        this.trackingManager.registrationStartPage,
      );
    }

    this.confirmationMethod = 'outgoing';
    this.setStep(RegisterStep.CONFIRM_PHONE);
  }

  submitRegister() {
    this.yandexCloudManager.clearRegistrationError();

    if (!this.yandexCloudManager.refreshValidation()) {
      return this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
    }

    if (this.validator.hasErrors) {
      return this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
    }

    this.setFormStatus(SubmittingStatus.SUBMITTING);
    this.clearRegisterErrors();

    switch (this.confirmationMethod) {
      case 'outgoing':
        this.registrationId = this.outgoingManager.registrationId;
        break;
      case 'incoming':
        this.registrationId = this.incomingConfirmationManager.registrationId;
        break;
      case 'qr':
        this.registrationId = this.qrConfirmationManager.registrationId;
        break;
    }

    const regData: RegisterRequest = {
      company_name: this.companyName ?? '',
      email: this.login as LoginEmail,
      name: this.fio as FIO,
      password: this.password as LoginPassword,
      phone_registration_id: this.registrationId ?? 'empty',
      phone: this.phone as PhoneNumber,
      promo: this.promoCode && this.promoCode.trim() ? this.promoCode.trim() : undefined,
      tz_offset: new Date().getTimezoneOffset(),
      is_operator: this.isOperator,
      ...this.trackingManager.registrationFields,
    };
    const yandexCloud = this.yandexCloudManager.data;
    if (yandexCloud) {
      regData.yandex_cloud = yandexCloud;
    }

    this.datasource.register(regData).then(accountResponse => {
      if (accountResponse.status !== ResponseStatus.SUCCESS) {
        this.handleRegisterError(accountResponse);
        return;
      }

      const { accounts_data, accounts_url, headers } = accountResponse.result;
      this.datasource
        .createWithAccount(accounts_url, accounts_data, headers)
        .then(createResponse => {
          if (createResponse.status === ResponseStatus.SUCCESS) {
            this.setFormStatus(SubmittingStatus.UNTOUCHED);
            this.metrikaService.reachGoal(
              RegistrationGoal.PROFILE_SUBMIT,
              this.trackingManager.registrationStartPage,
            );
            this.trackingManager.clear();
            this.autoLogin(this.login as LoginEmail, this.password as LoginPassword);
            return;
          }

          this.handleRegisterError(createResponse);
        });
    });
  }

  private handleRegisterError(response) {
    this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);

    if (response.status === ResponseStatus.SERVICE_ERROR) {
      const errorData = response.error.errorData;
      if (errorData.errors) {
        const { yandex_cloud: yandexCloudError, ...registerErrors } = errorData.errors;
        if (yandexCloudError && this.yandexCloudManager.setRegistrationError(yandexCloudError)) {
          return;
        }
        if (yandexCloudError) {
          this.registerGeneralError = yandexCloudError;
        }
        this.registerErrors = registerErrors;
        return;
      }
      if (errorData.error) {
        if (this.yandexCloudManager.setRegistrationError(errorData.error)) {
          return;
        }
        this.registerGeneralError = errorData.error;
      }
    }
  }
}

export { RegisterManager };
